<template>
  <div class="achievement-config" v-loading="loading">
    <div class="page-header">
      <div>
        <h2>成就图片资源</h2>
        <span class="muted">Fantasy · 资源版本 {{ resources.revision || '—' }}</span>
      </div>
      <div class="toolbar">
        <el-button :disabled="busy" @click="refresh">刷新</el-button>
        <el-button :disabled="busy || !catalog.revision" @click="catalogImportOpen = true">导入展示资料 JSON</el-button>
        <el-button :disabled="busy || !catalog.revision" @click="exportCatalog">导出展示资料</el-button>
        <el-button type="primary" :disabled="busy || !original" @click="openAddEntry">新增成就</el-button>
        <el-button :disabled="busy || !rows.length" @click="batchOpen = true">批量上传徽章 ZIP</el-button>
        <el-button :disabled="busy || !rows.length" :loading="validating" @click="validate">校验资源</el-button>
        <el-button type="success" :disabled="busy || !dirty || !ready" :loading="publishing" @click="publish">发布图片资源</el-button>
      </div>
    </div>
    <el-alert type="info" :closable="false" show-icon title="图片发布后供客户端使用；分组、阶段、奖励及多语言资料可编辑，仅供运营参考，实际规则仍由客户端发版确定。" />
    <el-alert v-if="loadError" type="error" :closable="false" :title="loadError" />
    <div class="summary">
      <el-tag>成就 {{ rows.length }}</el-tag>
      <el-tag v-if="rows.some(row => row.reference_only)" type="info">仅参考 {{ rows.filter(row => row.reference_only).length }}</el-tag>
      <el-tag :type="pendingBadges ? 'warning' : 'success'">本次资源待补徽章 {{ pendingBadges }}</el-tag>
      <el-tag :type="pendingRewards ? 'warning' : 'success'">待补奖励图 {{ pendingRewards }}</el-tag>
      <el-tag v-if="dirty" type="warning">本页有未发布修改</el-tag>
    </div>
    <el-card shadow="never">
      <div class="filters">
        <el-input v-model="query" clearable placeholder="搜索成就名称、描述或 ID" style="width: 300px" />
        <el-select v-model="group" clearable placeholder="成就分组" style="width: 160px">
          <el-option v-for="(label, key) in groups" :key="key" :label="label" :value="key" />
        </el-select>
        <el-checkbox v-model="pendingOnly">只看待补图片</el-checkbox>
        <span class="muted">共 {{ filteredRows.length }} 条</span>
      </div>
      <el-table :data="pagedRows" border size="small">
        <el-table-column label="名称" min-width="180">
          <template #default="{row}"><strong>{{ name(row) }}</strong><div v-if="row.name !== name(row)" class="muted">{{ row.name }}</div></template>
        </el-table-column>
        <el-table-column label="分组" width="110"><template #default="{row}">{{ groups[row.display_group] || row.section_type || '资源条目' }}</template></el-table-column>
        <el-table-column label="阶段" width="110"><template #default="{row}">{{ row.levels.length || '待填写' }}</template></el-table-column>
        <el-table-column label="徽章图片" width="170">
          <template #default="{row}">
            <img v-for="state in ['locked', 'unlocked']" :key="state" :src="imageUrl(row.display_images[state + '_thumbnail'] || row.display_images[state])" :alt="state === 'locked' ? '锁定图' : '点亮图'" class="badge-preview" v-show="row.display_images[state]">
            <el-tag v-if="row.reference_only" size="small" type="info">仅参考，未加入发布</el-tag>
            <el-tag v-else-if="!badgeReady(row.display_images)" size="small" type="warning">待补齐</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="条件说明（参考）" min-width="260"><template #default="{row}">{{ description(row) || '待填写' }}</template></el-table-column>
        <el-table-column label="操作" width="130"><template #default="{row}"><el-button size="small" :disabled="busy" @click="openDetail(row)">编辑 / 配图</el-button></template></el-table-column>
      </el-table>
      <el-pagination v-if="filteredRows.length > pageSize" v-model:current-page="page" :page-size="pageSize" :total="filteredRows.length" layout="total, prev, pager, next" />
    </el-card>
    <el-dialog v-model="detailOpen" :title="selected ? name(selected) : '成就资源'" width="min(960px, 94vw)" :close-on-click-modal="false" :show-close="!busy" :close-on-press-escape="!busy" :before-close="closeDetail">
      <template v-if="selected">
        <el-tabs v-model="detailTab"><el-tab-pane label="图片与名称" name="images">
          <details class="technical-details"><summary>查看技术标识</summary>{{ selected.achievement_id }}</details>
          <div v-if="selected.reference_only" class="resource-metadata">
            <el-alert type="info" :closable="false" title="此条目仅供参考，不影响已有图片发布。开始配图后会加入本次资源，请补齐徽章和奖励图片再发布。" />
            <el-button type="primary" :disabled="busy" @click="startReferenceEntry(selected.achievement_id)">开始配图</el-button>
          </div>
          <achievement-display-image-uploader :model-value="selected.display_images" :disabled="loading || validating || publishing || batchBusy || selected.reference_only" @update:model-value="updateBadge(selected.achievement_id, $event)" @uploading="uploading = $event" />
          <el-form label-width="110px" class="resource-metadata">
            <el-form-item label="运营名称"><el-input :model-value="selected.operator_name" :disabled="busy || selected.reference_only" maxlength="200" @update:model-value="updateMetadata('name', $event)" /></el-form-item>
            <el-form-item label="运营备注"><el-input :model-value="selected.operator_note" :disabled="busy || selected.reference_only" type="textarea" maxlength="2000" @update:model-value="updateMetadata('note', $event)" /></el-form-item>
          </el-form>
          <div v-for="slot in selectedSlots" :key="slot.resource_key" class="reward-slot">
            <strong>{{ slot.label }}</strong>
            <details class="technical-details"><summary>查看技术标识</summary>{{ slot.resource_key }}</details>
            <achievement-image-selector :model-value="resources.reward_slots[slot.resource_key] || ''" :disabled="busy || selected.reference_only" @update:model-value="resources.reward_slots[slot.resource_key] = $event" />
          </div>
        </el-tab-pane>
        <el-tab-pane label="参考资料" name="reference">
          <achievement-reference-fields v-if="referenceEditor" :draft="referenceEditor" :disabled="busy" />
        </el-tab-pane></el-tabs>
      </template>
      <template #footer>
        <template v-if="detailTab === 'reference'">
          <span class="muted">只保存参考资料，不发布图片。</span>
          <el-button type="primary" :disabled="busy || !referenceDirty || !catalog.revision" :loading="catalogSaving" @click="saveReference">保存参考资料</el-button>
        </template>
        <span v-else class="muted">图片和运营名称暂存于本页，关闭后统一发布。</span>
        <el-button :disabled="busy" @click="closeDetail()">完成</el-button>
      </template>
    </el-dialog>
    <el-dialog v-model="addOpen" title="新增成就" width="min(960px, 94vw)"
      :close-on-click-modal="false" :show-close="!busy" :close-on-press-escape="!busy" :before-close="closeNewEntry">
      <el-form label-width="80px" class="entry-form" :disabled="publishing">
        <el-form-item label="成就标识">
          <el-input v-model="newEntry.id" :disabled="newReferenceSaved" placeholder="填写与客户端约定的标识，例如 collection_neon_wonderland" maxlength="128" />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="newEntry.name" placeholder="填写成就名称，方便查找" maxlength="200" />
        </el-form-item>
        <el-form-item label="备注"><el-input v-model="newEntry.note" type="textarea" maxlength="2000" placeholder="运营备注（可选）" /></el-form-item>
      </el-form>
      <achievement-display-image-uploader v-model="newEntry.images"
        :disabled="publishing" @uploading="uploading = $event" />
      <p class="muted">成就标识须与客户端一致，保存后保持不变；名称只用于 CMS 查找，不下发给客户端。</p>
      <el-collapse><el-collapse-item title="参考资料（可选）：分组、阶段、条件、奖励和多语言" name="reference">
        <achievement-reference-fields :draft="newReference" :disabled="busy || !catalog.revision" />
        <p v-if="!catalog.revision" class="muted">资料服务暂不可用，仍可先发布图片，稍后在编辑中补充资料。</p>
      </el-collapse-item></el-collapse>
      <el-alert v-if="newReferenceSaved" type="info" :closable="false" title="参考资料已保存，图片尚未发布；可继续重试发布图片。" />
      <template #footer>
        <el-button :disabled="busy" @click="closeNewEntry()">取消</el-button>
        <el-button type="primary" :disabled="busy || !newEntry.id.trim() || !newEntry.name.trim() || !badgeReady(newEntry.images)"
          :loading="publishing" @click="publishNewEntry">保存并发布</el-button>
      </template>
    </el-dialog>
    <el-dialog v-model="catalogImportOpen" title="导入成就展示资料" width="min(900px, 94vw)" :close-on-click-modal="false" :show-close="!catalogSaving">
      <el-alert type="info" :closable="false" title="资料保存于数据库，仅供 CMS 展示。不会更改图片绑定，也不会向客户端动态下发条件、奖励或文案。请导出当前资料修改后再导入，保留已有条目。" />
      <el-upload :auto-upload="false" :show-file-list="false" accept=".json,application/json" :disabled="catalogSaving" :on-change="readCatalogFile"><el-button :disabled="catalogSaving">选择 JSON 文件</el-button></el-upload>
      <el-input v-model="catalogImportText" type="textarea" :rows="18" :disabled="catalogSaving" placeholder="选择资料 JSON 文件，或粘贴完整 JSON" />
      <template #footer><el-button :disabled="catalogSaving" @click="catalogImportOpen = false">取消</el-button><el-button type="primary" :loading="catalogSaving" @click="importCatalog">保存展示资料</el-button></template>
    </el-dialog>
    <el-card v-if="unassignedSlots.length" class="reward-slot" shadow="never">
      <p>其他已有奖励图片（尚未关联成就资料，保留原绑定）</p>
      <div v-for="(key, index) in unassignedSlots" :key="key" class="reward-slot"><strong>奖励图片 {{ index + 1 }}</strong><details class="technical-details"><summary>查看技术标识</summary>{{ key }}</details><achievement-image-selector :model-value="resources.reward_slots[key] || ''" :disabled="busy" @update:model-value="resources.reward_slots[key] = $event" /></div>
    </el-card>
    <achievement-image-batch-uploader v-model="batchOpen" :achievements="rows" :busy="batchBusy" :progress="batchProgress" @submit="uploadBatch" />
  </div>
</template>

<script>
import {ElMessage, ElMessageBox} from 'element-plus';
import {saveAchievementCatalog, fetchAchievementCatalog, fetchAchievementResources, validateAchievementResources, publishAchievementResources, uploadAchievementImageResource} from '../libs/achievementApi';
import {resourceDraft, catalogRows, badgeReady, addResourceEntry, rewardKeys, rewardImageRows} from '../libs/achievementResources.mjs';
import {referenceDraft, catalogWithReference, achievementGroups} from '../libs/achievementReference.mjs';
import AchievementDisplayImageUploader from './AchievementDisplayImageUploader.vue';
import AchievementImageBatchUploader from './AchievementImageBatchUploader.vue';
import AchievementImageSelector from './AchievementImageSelector.vue';
import AchievementReferenceFields from './AchievementReferenceFields.vue';

export default {
  name: 'AchievementConfig',
  components: {AchievementDisplayImageUploader, AchievementImageBatchUploader, AchievementImageSelector, AchievementReferenceFields},
  data() {
    return {
      catalogImportOpen: false, catalogImportText: '', catalogSaving: false,
      catalog: {}, resources: {badges: {}, reward_slots: {}, entries: {}}, original: '', loading: false, loadError: '',
      uploading: false, validating: false, publishing: false, batchBusy: false,
      batchOpen: false, batchProgress: {completed: 0, total: 0}, detailOpen: false, selectedId: '',
      addOpen: false, newEntry: {id: '', name: '', note: '', images: {}},
      newReference: referenceDraft(), newReferenceSaved: false,
      detailTab: 'images', referenceEditor: null, referenceOriginal: '',
      query: '', group: '', pendingOnly: false, page: 1, pageSize: 20,
      groups: achievementGroups,
    };
  },
  computed: {
    rows() { return catalogRows(this.catalog, this.resources); },
    busy() { return this.catalogSaving || this.loading || this.uploading || this.validating || this.publishing || this.batchBusy; },
    dirty() { return Boolean(this.original) && JSON.stringify(this.resources) !== this.original; },
    pendingBadges() { return Object.values(this.resources.badges).filter(images => !badgeReady(images)).length; },
    pendingRewards() { return Object.values(this.resources.reward_slots).filter(value => !value).length; },
    ready() { return Object.keys(this.resources.badges).length > 0 && !this.pendingBadges && !this.pendingRewards; },
    filteredRows() {
      const query = this.query.trim().toLowerCase();
      return this.rows.filter(row => (!this.group || row.display_group === this.group)
        && (!query || `${this.name(row)} ${row.name} ${this.description(row)} ${row.achievement_id}`.toLowerCase().includes(query))
        && (!this.pendingOnly || !badgeReady(row.display_images) || rewardKeys(this.catalog, this.resources, row.achievement_id).some(key => !this.resources.reward_slots[key])));
    },
    pagedRows() { return this.filteredRows.slice((this.page - 1) * this.pageSize, this.page * this.pageSize); },
    selected() { return this.rows.find(row => row.achievement_id === this.selectedId); },
    selectedSlots() { return rewardImageRows(this.catalog, this.resources, this.selectedId); },
    referenceDirty() { return this.detailOpen && this.referenceEditor && JSON.stringify(this.referenceEditor) !== this.referenceOriginal; },
    hasNewEntryDraft() {
      return this.addOpen && Boolean(this.newEntry.id.trim() || this.newEntry.name.trim() || this.newEntry.note?.trim() || Object.values(this.newEntry.images).some(Boolean) || JSON.stringify(this.newReference) !== JSON.stringify(referenceDraft()));
    },
    unassignedSlots() {
      const assigned = new Set(this.rows.filter(row => !row.reference_only)
        .flatMap(row => rewardKeys(this.catalog, this.resources, row.achievement_id)));
      return Object.keys(this.resources.reward_slots).filter(key => !assigned.has(key));
    },
  },
  watch: {query() { this.page = 1; }, group() { this.page = 1; }, pendingOnly() { this.page = 1; }},
  mounted() { this.load(); window.addEventListener('beforeunload', this.beforeUnload); },
  beforeUnmount() { window.removeEventListener('beforeunload', this.beforeUnload); },
  async beforeRouteLeave() {
    if (this.busy) { ElMessage.warning('请等待上传或保存完成'); return false; }
    return !(this.dirty || this.hasNewEntryDraft || this.referenceDirty) || await this.confirmDiscard();
  },
  methods: {
    badgeReady,
    name(row) { return row.operator_name || row.i18n_name?.ZH_HANS || row.source_name_zh || row.name; },
    description(row) { return row.i18n_desc?.ZH_HANS || row.description || row.levels?.[0]?.i18n_desc?.ZH_HANS || row.levels?.[0]?.description || ''; },
    imageUrl(resource) {
      if (!resource) return '';
      if (/^https:\/\//i.test(resource)) return resource;
      return `${this.$root?.cdn_url || 'https://jigsaw-cdn.plutodesk.com'}/${String(resource).replace(/^\/+/, '')}`;
    },
    beforeUnload(event) { if (this.dirty || this.hasNewEntryDraft || this.referenceDirty || this.busy) { event.preventDefault(); event.returnValue = ''; } },
    async confirmDiscard() {
      try { await ElMessageBox.confirm('本页有未发布的图片修改，是否放弃？', '未发布修改', {confirmButtonText: '放弃修改', cancelButtonText: '继续编辑', type: 'warning'}); return true; } catch (_) { return false; }
    },
    async refresh() { if (!this.dirty || await this.confirmDiscard()) await this.load(); },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const [catalogResult, resourcesResult] = await Promise.allSettled([fetchAchievementCatalog(), fetchAchievementResources()]);
        if (resourcesResult.status === 'rejected') throw resourcesResult.reason;
        const draft = resourceDraft(resourcesResult.value);
        // 展示资料是可选说明，读取失败不能挡住图片资源管理。
        this.catalog = catalogResult.status === 'fulfilled' ? catalogResult.value : {};
        if (catalogResult.status === 'rejected') ElMessage.warning('参考资料读取失败，仍可新增和编辑图片资源');
        this.resources = draft;
        this.original = JSON.stringify(draft);
        this.page = 1;
      } catch (error) { this.loadError = this.errorMessage(error, '资源读取失败，请刷新重试'); } finally { this.loading = false; }
    },
    exportCatalog() {
      const blob = new Blob([JSON.stringify(this.catalog, null, 2)], {type: 'application/json'});
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = 'achievement-catalog.json'; anchor.click();
      URL.revokeObjectURL(url);
    },
    async readCatalogFile(file) {
      try { this.catalogImportText = await file.raw.text(); }
      catch (_) { ElMessage.error('读取资料文件失败'); }
    },
    async importCatalog() {
      let catalog;
      try {
        catalog = JSON.parse(this.catalogImportText);
        if (!Array.isArray(catalog.achievements)) throw new Error('资料必须包含 achievements 数组');
        if (this.dirty) { ElMessage.warning('请先发布图片修改或刷新放弃修改，再导入资料'); return; }
        await ElMessageBox.confirm(`将保存 ${catalog.achievements.length} 条展示资料；图片绑定和客户端行为不变。是否继续？`, '保存展示资料', {type: 'warning'});
      } catch (error) { if (error instanceof Error) ElMessage.error(error.message); return; }
      this.catalogSaving = true;
      try {
        this.catalog = await saveAchievementCatalog(catalog, this.catalog.revision);
        this.catalogImportOpen = false; this.catalogImportText = '';
        ElMessage.success('展示资料已保存；客户端条件、奖励和文案仍随发版生效');
        await this.load();
      } catch (error) { ElMessage.error(this.errorMessage(error, '资料保存失败；请刷新后重试')); }
      finally { this.catalogSaving = false; }
    },
    errorMessage(error, fallback) { return error?.response?.data?.status?.message || error?.response?.data?.message || error?.message || fallback; },
    openAddEntry() {
      if (this.dirty) {
        ElMessage.warning('请先发布当前修改，或刷新放弃修改，再新增资源');
        return;
      }
      this.newEntry = {id: '', name: '', note: '', images: {}};
      this.newReference = referenceDraft();
      this.newReferenceSaved = false;
      this.addOpen = true;
    },
    async closeNewEntry(done) {
      if (this.busy) return;
      if (this.hasNewEntryDraft && !await this.confirmDiscard()) return;
      this.addOpen = false;
      if (typeof done === 'function') done();
    },
    async publishNewEntry() {
      if (this.busy || this.dirty || !this.newEntry.id.trim() || !this.newEntry.name.trim() || !badgeReady(this.newEntry.images)) return;
      this.publishing = true;
      try {
        // 新增草稿独立于已发布资源；上传未完成或发布失败都不会混入空图片。
        const id = this.newEntry.id.trim();
        // 每次尝试基于最新资源追加，保留其他运营刚发布的修改，服务端仍校验 revision。
        const latest = await fetchAchievementResources();
        const draft = resourceDraft(latest);
        addResourceEntry(draft, {id, name: this.newEntry.name, note: this.newEntry.note});
        draft.badges[id] = {...this.newEntry.images};
        await validateAchievementResources(draft);
        if (JSON.stringify(this.newReference) !== JSON.stringify(referenceDraft())) {
          if (!this.catalog.revision) throw new Error('参考资料未加载，请刷新后重试');
          if (!this.newReferenceSaved && this.catalog.achievements?.some(item => item.achievement_id === id)) {
            throw new Error('该标识已有参考资料，请关闭新增窗口，在原条目中编辑和配图');
          }
          const updated = catalogWithReference(this.catalog, this.newReference, id, this.newEntry.name, draft);
          this.catalog = await saveAchievementCatalog(updated, this.catalog.revision);
          this.newReference = referenceDraft(this.catalog.achievements.find(item => item.achievement_id === id));
          this.newReferenceSaved = true;
        }
        const published = await publishAchievementResources(draft, latest.revision);
        this.resources = resourceDraft(published);
        this.original = JSON.stringify(this.resources);
        this.addOpen = false;
        this.query = this.newEntry.id.trim();
        this.group = '';
        this.pendingOnly = false;
        this.page = 1;
        ElMessage.success('图片资源已发布，可在条目中查看固定资源标识');
      } catch (error) {
        const message = this.errorMessage(error, '请重试；若提示标识已存在，请关闭后刷新核对');
        ElMessage.error(`${this.newReferenceSaved ? '参考资料已保存，图片发布未完成。' : ''}${message}`);
      } finally { this.publishing = false; }
    },
    openDetail(row) {
      this.selectedId = row.achievement_id;
      const definition = this.catalog.achievements?.find(item => item.achievement_id === this.selectedId);
      this.referenceEditor = referenceDraft(definition || {name: this.name(row)});
      this.referenceOriginal = JSON.stringify(this.referenceEditor);
      this.detailTab = 'images';
      this.detailOpen = true;
    },
    async closeDetail(done) {
      if (this.busy) return;
      if (this.referenceDirty) {
        try { await ElMessageBox.confirm('参考资料尚未保存，是否放弃本次资料修改？', '未保存资料', {confirmButtonText: '放弃修改', cancelButtonText: '继续编辑'}); }
        catch (_) { return; }
      }
      this.detailOpen = false;
      if (typeof done === 'function') done();
    },
    async saveReference() {
      if (this.busy || !this.referenceDirty || !this.catalog.revision) return;
      this.catalogSaving = true;
      try {
        const updated = catalogWithReference(this.catalog, this.referenceEditor, this.selectedId, this.name(this.selected), this.resources);
        this.catalog = await saveAchievementCatalog(updated, this.catalog.revision);
        this.referenceEditor = referenceDraft(this.catalog.achievements.find(item => item.achievement_id === this.selectedId));
        this.referenceOriginal = JSON.stringify(this.referenceEditor);
        ElMessage.success('参考资料已保存，客户端规则和已发布图片不变');
      } catch (error) { ElMessage.error(this.errorMessage(error, '资料保存失败，请刷新后重试')); }
      finally { this.catalogSaving = false; }
    },
    updateMetadata(field, value) {
      const id = this.selectedId;
      this.resources.entries[id] ||= {name: '', note: '', reward_keys: []};
      this.resources.entries[id][field] = value;
    },
    startReferenceEntry(id) {
      if (Object.hasOwn(this.resources.badges, id)) return;
      const row = this.rows.find(item => item.achievement_id === id);
      const keys = rewardKeys(this.catalog, this.resources, id);
      addResourceEntry(this.resources, {id, name: row?.name || '',
        reward_keys: keys.filter(key => !Object.hasOwn(this.resources.reward_slots, key))});
      // 已有槽位可以被资料关联，保留其图片，只补本次明确选择的新槽位。
      this.resources.entries[id].reward_keys = keys;
    },
    updateBadge(id, images) {
      this.startReferenceEntry(id);
      this.resources.badges[id] = {...images};
    },
    async validate() {
      this.validating = true;
      try { await validateAchievementResources(resourceDraft(this.resources)); ElMessage.success('资源校验通过'); }
      catch (error) { ElMessage.error(this.errorMessage(error, '资源校验失败')); }
      finally { this.validating = false; }
    },
    async publish() {
      try { await ElMessageBox.confirm('发布后，客户端将使用本次图片资源。是否发布？', '发布图片资源', {confirmButtonText: '发布', cancelButtonText: '取消'}); } catch (_) { return; }
      this.publishing = true;
      try {
        const draft = resourceDraft(this.resources);
        await validateAchievementResources(draft);
        const published = await publishAchievementResources(draft, this.resources.revision);
        this.resources = resourceDraft(published);
        this.original = JSON.stringify(this.resources);
        ElMessage.success('图片资源已发布');
      } catch (error) { ElMessage.error(this.errorMessage(error, '发布失败；若版本已变化，请刷新后重新编辑')); }
      finally { this.publishing = false; }
    },
    async uploadBatch(rows) {
      this.batchBusy = true;
      this.batchProgress = {completed: 0, total: rows.reduce((sum, row) => sum + Number(Boolean(row.lockedFile)) + Number(Boolean(row.unlockedFile)), 0)};
      try {
        for (const row of rows) {
          for (const state of ['locked', 'unlocked']) {
            const file = row[`${state}File`];
            if (!file) continue;
            const image = await uploadAchievementImageResource(file);
            this.updateBadge(row.achievementId, {...this.resources.badges[row.achievementId], [state]: image.detail, [`${state}_thumbnail`]: image.thumbnail});
            this.batchProgress.completed++;
          }
        }
        this.batchOpen = false;
        ElMessage.success('徽章已上传，请校验后发布');
      } catch (error) { ElMessage.error(this.errorMessage(error, '部分图片上传失败；已成功上传的图片保留在本页，可重试')); }
      finally { this.batchBusy = false; }
    },
  },
};
</script>

<style scoped>
.page-header, .toolbar, .filters, .summary { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.page-header { justify-content: space-between; margin-bottom: 16px; }
h2 { margin: 0 0 6px; }
.muted { color: var(--el-text-color-secondary); font-size: 12px; overflow-wrap: anywhere; }
.summary, .filters { margin: 16px 0; }
.badge-preview { width: 48px; height: 48px; object-fit: contain; margin-right: 8px; }
.entry-form { margin-top: 20px; }
.technical-details { margin: 8px 0 12px; color: var(--el-text-color-secondary); font-size: 12px; overflow-wrap: anywhere; }
.technical-details summary { cursor: pointer; }
.reward-slot { margin: 18px 0; padding: 12px; border: 1px solid var(--el-border-color); border-radius: 6px; }
.reward-slot > strong { display: block; margin-bottom: 10px; }
.el-pagination { margin-top: 16px; }
.el-alert + .el-alert { margin-top: 10px; }
</style>
